import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { Lock, Eye, EyeOff } from "lucide-react";

import { Button, TextField } from "../../../ui/index.ts";
import {
  resetPasswordSchema,
  type ResetPasswordValues,
} from "../schemas/index.ts";
import { useResetPassword } from "../hooks/use-reset-password.ts";
import { formatFormFieldErrors } from "./form-errors.ts";
import styles from "./login-form.module.css";

export function ResetPasswordForm() {
  const { loading, error, reset } = useResetPassword();
  const { state } = useLocation();
  const { email, token } = (state as { email?: string; token?: string }) || {};

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm({
    defaultValues: { password: "", confirmPassword: "" } as ResetPasswordValues,
    validators: { onSubmit: resetPasswordSchema },
    onSubmit: async ({ value }) => {
      if (!email || !token) {
        // This shouldn't normally happen if they came from the forgot password flow
        return;
      }
      await reset(value, email, token);
    },
  });

  if (!email || !token) {
    return (
      <div className={styles.form}>
        <p className={`${styles.errorBanner} body-sm`}>
          Invalid session. Please start the password reset process again.
        </p>
        <Link to="/forgot-password" className={`${styles.forgotLink} body-sm`}>
          Go to Forgot Password
        </Link>
      </div>
    );
  }

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >
      {error != null ? (
        <p className={`${styles.errorBanner} body-sm`} role="alert">
          {error}
        </p>
      ) : null}

      <form.Field name="password">
        {(field) => (
          <div className={styles.passwordField}>
            <span className={styles.fieldLeadIcon} aria-hidden="true">
              <Lock size={18} />
            </span>
            <TextField
              label="New Password"
              id="new-password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter at least 8 characters"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              error={formatFormFieldErrors(field.state.meta.errors)}
              disabled={loading}
              className={`${styles.passwordInput} ${styles.inputElevated}`}
            />
            <button
              type="button"
              className={styles.toggleButton}
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        )}
      </form.Field>

      <form.Field name="confirmPassword">
        {(field) => (
          <div className={styles.passwordField}>
            <span className={styles.fieldLeadIcon} aria-hidden="true">
              <Lock size={18} />
            </span>
            <TextField
              label="Confirm Password"
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Re-enter your new password"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              error={formatFormFieldErrors(field.state.meta.errors)}
              disabled={loading}
              className={`${styles.passwordInput} ${styles.inputElevated}`}
            />
            <button
              type="button"
              className={styles.toggleButton}
              onClick={() => setShowConfirmPassword((s) => !s)}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        )}
      </form.Field>

      <div className={styles.actions}>
        <Button
          type="submit"
          fullWidth
          disabled={loading}
          className={styles.submitButton}
          startIcon={
            loading ? <span className={styles.spinner} aria-hidden="true" /> : undefined
          }
        >
          {loading ? "Resetting\u2026" : "Update Password"}
        </Button>

        <Link to="/" className={`${styles.forgotLink} body-sm`}>
           Back to Login
        </Link>
      </div>
    </form>
  );
}
